import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  ARCHIVED_ANALYSIS_TOOLTIP_TEXT,
  SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
} from 'analysis-tool/AnalysisToolConstants';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_PROXIMA_11, FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {smallSpacingTop} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import CopyAnalysisKeyButton from './CopyAnalysisKeyButton';
import {SharedThirdPartyStepHeaderProps} from './Types';

const SUPPORTED_STATUS_CODES = [
  PipelineStepStatusCode.SUCCEEDED,
  PipelineStepStatusCode.WAITING,
  PipelineStepStatusCode.FAILED,
] as const;

type SupportedStatusCode = (typeof SUPPORTED_STATUS_CODES)[number];

function statusCodeIsSupported(statusCode: PipelineStepStatusCode): statusCode is SupportedStatusCode {
  return SUPPORTED_STATUS_CODES.includes(statusCode as SupportedStatusCode);
}

export default function ThirdPartyStepHeader({analysis, step}: SharedThirdPartyStepHeaderProps): JSX.Element {
  // For the LA Analysis flow, only certain step statuses are possible.
  assert(statusCodeIsSupported(step.statusCode), 'Unexpected status code for third party step', {
    statusCode: step.statusCode,
  });

  const statusCodeToBannerProps: {[key in SupportedStatusCode]: InlineBannerProps} = {
    [PipelineStepStatusCode.SUCCEEDED]: {
      bannerType: InlineBannerType.SUCCESS,
      text: 'Successfully received data.',
    },
    [PipelineStepStatusCode.WAITING]: {
      bannerType: InlineBannerType.INFO,
      text: 'Awaiting output from external tool.',
    },
    [PipelineStepStatusCode.FAILED]: {
      bannerType: InlineBannerType.ERROR,
      text: 'Error receiving data.',
      supportingInfo: step.statusMessage,
    },
  };

  const bannerProps = statusCodeToBannerProps[step.statusCode];

  return (
    <div
      className={css(styles.viewWrapper)}
      {...getTestProps({
        component: 'ThirdPartyStepHeader',
        element: 'root',
      })}
    >
      <div className="u-flex-display">
        <div className={css(styles.headerIconWrapper)}>
          <BenchlingIcon icon="integration" className={css(styles.headerIcon)} />
        </div>
        <div className="u-small-spacing-left">
          <div className={css(styles.title)}>
            {STEP_TYPE_TO_USER_FACING_NAME[PipelineStepType.THIRD_PARTY]}
          </div>
          <div className={css(styles.subtitle)}>Last modified: {useFuzzyDate(step.modifiedAt)}</div>
        </div>
      </div>
      <div className={css(styles.banner)}>
        <InlineBanner
          {...bannerProps}
          dataTestAttrs={{
            component: 'ThirdPartyStepHeader',
            element: 'banner',
          }}
        />
      </div>
      {(analysis.permissions.writable || analysis.permissions.unarchivable) && (
        <div className={css(styles.copyStepKeyButton)}>
          <Tooltip
            title={
              isArchived(analysis)
                ? ARCHIVED_ANALYSIS_TOOLTIP_TEXT
                : 'Depending on your external tool’s connection, you may need this Analysis key.'
            }
            placement={TooltipPlacement.TOP}
          >
            <CopyAnalysisKeyButton analysisId={analysis.id} disabled={isArchived(analysis)} />
          </Tooltip>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  viewWrapper: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    padding: '20px 20px 16px 20px',
    backgroundColor: 'white',
  },
  headerIcon: {
    height: 23,
    width: 23,
  },
  headerIconWrapper: {
    backgroundColor: GRAY_LIGHTER,
    height: 40,
    width: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subtitle: {
    ...FONT_PROXIMA_11,
    color: GRAY_DARKER,
  },
  title: {
    ...FONT_PROXIMA_16,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  banner: {
    ...smallSpacingTop,
  },
  copyStepKeyButton: {
    marginTop: 16,
  },
});
